/**
 * Options passed through to the Agent `init` call.
 *
 * Note on error handling:
 * - When signal collection times out, the Agent rejects with an error whose
 *   message includes compact codes like "[group][part]" and exposes two fields:
 *     - `codes`: Array<[group, part]> numeric pairs for quick parsing
 *     - `pending`: Array of pending task descriptors { group, part, name? }
 * - The SDK forwards your `onError` callback directly, no transformation.
 */
export type AgentInitOptions = {
  siteKey: string;
  debug?: boolean;
  /** Max time to wait for signal collection before rejecting (ms). */
  collectionTimeoutMs?: number;
  /**
   * Invoked when signal collection fails (e.g., timeout).
   * On timeout, the error will typically be a `GuardianSignalCollectionTimeoutError`
   * with `error.codes` and `error.pending` properties available via duck typing.
   */
  onError?: (error: Error) => void;
};

export type LoadAgentOptions = AgentInitOptions & {
  src?: string; // CDN URL; defaults to latest alias
  /**
   * Optional timeout (ms) for loading the agent script per candidate URL.
   * Prevents hanging indefinitely on stalled networks/CDNs.
   * Defaults to 5000 ms.
   */
  loadTimeoutMs?: number;
  /**
   * When true (default), appends a cache-busting timestamp to the agent URL to
   * avoid stale CDN caches. Set to false to allow normal caching.
   */
  noCache?: boolean;
};

// Structured event result returned by the Agent
export type EventResponse = {
  requestId: string;
  status: number;
  body?: any;
  response: Response;
};

declare global {
  interface Window {
    FraudDetectionAgent?: {
      init: (options?: AgentInitOptions) => {
        getEndpoint: () => string | undefined;
        get: (extra?: any) => Promise<EventResponse | undefined>;
        getFullSignals?: () => Promise<any>;
      };
    };
    FraudDetectionSDK?: {
      loadAgent: typeof loadAgent;
    };
  }
}

// Default to your deployed CDN URL (S3 public URL or CloudFront)
const DEFAULT_SRC =
  "https://agents-js.s3.eu-north-1.amazonaws.com/agent/latest/agent.global.js";

function defaultSrcCandidates(): string[] {
  const candidates: string[] = [];
  // Prefer CDN first so consumers don't need to host the agent file locally
  candidates.push(DEFAULT_SRC);
  // Local fallbacks for SDK dev/examples or app-served agent
  candidates.push("agent/agent.global.js");
  candidates.push("/public/agent/agent.global.js");
  candidates.push("/agent/agent.global.js");
  candidates.push("/public/agent/agent.global.js");
  return candidates;
}

function injectScript(url: string, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("No document to inject script into"));
      return;
    }
    // If the exact URL is already present, treat as loaded
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    let settled = false;
    let timer: any;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      s.onload = null;
      s.onerror = null;
    };
    s.async = true;
    s.src = url;
    s.onload = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    s.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      // Best-effort cleanup of the failed tag
      try {
        s.parentElement?.removeChild(s);
      } catch {}
      reject(new Error(`Failed to load ${url}`));
    };
    if (timeoutMs > 0 && typeof timeoutMs === "number" && isFinite(timeoutMs as number)) {
      timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        try {
          s.parentElement?.removeChild(s);
        } catch {}
        const err = new Error(`Timed out loading ${url}`);
        (err as any).name = "TimeoutError";
        reject(err);
      }, timeoutMs);
    }
    document.head.appendChild(s);
  });
}

function withNoCache(url: string): string {
  try {
    const base =
      typeof window !== "undefined" ? window.location.href : undefined;
    const u = new URL(url, base);
    u.searchParams.set("_ts", String(Date.now()));
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}_ts=${Date.now()}`;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type AgentAPI = ReturnType<
  NonNullable<NonNullable<typeof window>["FraudDetectionAgent"]>["init"]
>;

export type LoadedAgentAPI = AgentAPI & {
  getEndpoint: () => string | undefined;
};

async function waitForAgentGlobal(timeoutMs: number): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("FraudDetectionAgent: window is not available");
  }

  const hasAgent = () =>
    !!window.FraudDetectionAgent &&
    typeof window.FraudDetectionAgent.init === "function";

  // Fast path: agent is already present.
  if (hasAgent()) {
    return;
  }

  const isFiniteNumber =
    typeof timeoutMs === "number" && isFinite(timeoutMs as number);
  if (!isFiniteNumber || timeoutMs <= 0) {
    throw new Error("FraudDetectionAgent not available after script load");
  }

  const start = Date.now();
  const pollInterval = 50;

  while (Date.now() - start < timeoutMs) {
    if (hasAgent()) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  throw new Error("FraudDetectionAgent not available after script load");
}

export async function loadAgent(
  options: LoadAgentOptions
): Promise<LoadedAgentAPI> {
  const {
    src,
    siteKey,
    debug,
    loadTimeoutMs = 5000,
    noCache = true,
    collectionTimeoutMs,
    onError,
  } = options;

  if (!siteKey || typeof siteKey !== "string" || siteKey.trim() === "") {
    throw new Error("Site key is required");
  }

  const candidates = src ? [src] : defaultSrcCandidates();
  let loaded = false;
  let lastError: any = null;

  // Internal, bounded retry policy for the primary (typically CDN) URL.
  const maxPrimaryRetries = 3; // 3 additional attempts => up to 4 total tries.
  const totalPrimaryAttempts = 1 + maxPrimaryRetries;
  const retryDelayMs = 250;

  for (let index = 0; index < candidates.length; index++) {
    const candidate = candidates[index];
    // Apply retries only to the primary (typically CDN) URL; fallbacks are
    // tried once to avoid long total load times when they are not present.
    const attemptsForThisCandidate =
      index === 0 ? totalPrimaryAttempts : 1;

    for (let attempt = 0; attempt < attemptsForThisCandidate; attempt++) {
      const urlToLoad = noCache ? withNoCache(candidate) : candidate;
      try {
        if (debug && typeof console !== "undefined" && console.debug) {
          console.debug(
            "[FraudDetectionSDK] Loading agent script",
            { url: urlToLoad, attempt: attempt + 1, attemptsForThisCandidate }
          );
        }

        await injectScript(urlToLoad, loadTimeoutMs);
        loaded = true;
        break;
      } catch (err) {
        lastError = err;

        if (debug && typeof console !== "undefined" && console.warn) {
          console.warn(
            "[FraudDetectionSDK] Failed to load agent script",
            { url: urlToLoad, attempt: attempt + 1, attemptsForThisCandidate },
            err
          );
        }

        const isLastAttemptForCandidate =
          attempt === attemptsForThisCandidate - 1;

        if (isLastAttemptForCandidate) {
          // Move on to the next candidate URL.
          break;
        }

        if (retryDelayMs > 0) {
          await sleep(retryDelayMs);
        }
      }
    }

    if (loaded) {
      break;
    }
  }

  if (!loaded) {
    throw lastError || new Error("Failed to load agent script");
  }

  // At this point the script tag has loaded successfully, but depending on how
  // the page and network behave the global might not yet be attached. Wait for
  // it up to the configured timeout instead of failing immediately, which
  // previously forced users to reload the page.
  await waitForAgentGlobal(loadTimeoutMs);

  const agentApi = window.FraudDetectionAgent!.init({
    siteKey,
    debug,
    collectionTimeoutMs,
    onError,
  });

  const api: LoadedAgentAPI = {
    ...agentApi,
    getEndpoint: () => agentApi.getEndpoint?.(),
  };

  return api;
}

// Attach global helper for IIFE builds
if (typeof window !== "undefined") {
  const sdkGlobal: any = (window as any).FraudDetectionSDK || {};
  sdkGlobal.loadAgent = loadAgent;
  (window as any).FraudDetectionSDK = sdkGlobal;
}
